import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import type { Maybe } from '__generated__/globalTypes';
import { PaymentMethodProvider } from '__generated__/globalTypes';
import type { CurrentUserProvider_currentUser } from 'contexts/currentUser/__generated__/queries.graphql';
import { useQuery } from 'hooks/graphql/useQuery';
import { useMangopayCreditCardsEnabled } from 'hooks/useMangopayCreditCardsEnabled';

import type {
  PaymentProvider_currentUserQuery,
  PaymentProvider_currentUserQueryVariables,
} from './__generated__/useCurrentUsersPaymentMethods.graphql';

const PAYMENT_PROVIDER_CURRENT_USER_QUERY = gql`
  query PaymentProvider_currentUserQuery($provider: PaymentMethodProvider) {
    currentUser {
      slug
      paymentMethods(provider: $provider) {
        id
        card {
          last4
          brand
          type
        }
      }
    }
  }
` as TypedDocumentNode<
  PaymentProvider_currentUserQuery,
  PaymentProvider_currentUserQueryVariables
>;

type PaymentProvider_currentUserQuery_currentUser_paymentMethods = NonNullable<
  PaymentProvider_currentUserQuery['currentUser']
>['paymentMethods'][number];

export const useCurrentUsersPaymentMethods = (
  currentUser: Maybe<CurrentUserProvider_currentUser> | undefined,
  specifiedProvider?: PaymentMethodProvider
): [
  PaymentProvider_currentUserQuery_currentUser_paymentMethods[] | null,
  boolean,
  () => void,
] => {
  const useMangopayCreditCards = useMangopayCreditCardsEnabled();
  const provider = useMemo(() => {
    if (specifiedProvider) return specifiedProvider;
    if (useMangopayCreditCards) return PaymentMethodProvider.MANGOPAY;
    return PaymentMethodProvider.STRIPE;
  }, [useMangopayCreditCards, specifiedProvider]);
  const { data, loading, refetch } = useQuery(
    PAYMENT_PROVIDER_CURRENT_USER_QUERY,
    {
      variables: {
        provider,
      },
      skip: !currentUser,
    }
  );

  return [data?.currentUser?.paymentMethods || null, loading, refetch];
};
